/**
 * Message Variant Analytics (MA-IMPL-126B)
 * 
 * Displays message variant performance metrics using the lineage chain:
 * campaign → funnel → lead.message_variant → appointment → client → revenue
 * 
 * Tracks the core messaging hypothesis across the entire system.
 * Does NOT infer from campaign at query time - uses snapshotted values only.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MessageSquare,
  TrendingUp,
  DollarSign,
  Users,
  Target,
  ChevronDown,
  AlertCircle,
  Trophy,
  AlertTriangle,
  Filter,
  Sparkles,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { api } from '../../lib/api';
import { HELPER_DEFINITIONS } from './HelperText';

// =============================================================================
// API HOOKS
// =============================================================================

function useMessageVariantPerformance({ period, sortBy, workspaceId, campaignId, vertical, channel, tenantId }) {
  return useQuery({
    queryKey: ['message-variant-performance', period, sortBy, workspaceId, campaignId, vertical, channel, tenantId],
    queryFn: async () => {
      const params = new URLSearchParams({ period, sort_by: sortBy });
      if (workspaceId) params.append('workspace_id', workspaceId);
      if (campaignId && campaignId !== 'all') params.append('campaign_id', campaignId);
      if (vertical && vertical !== 'all') params.append('vertical', vertical);
      if (channel && channel !== 'all') params.append('channel', channel);
      if (tenantId) params.append('tenant_id', tenantId);
      
      const res = await api.get(`/analytics/message-variant-performance?${params}`);
      return res.data;
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
}

// =============================================================================
// FORMATTERS
// =============================================================================

function formatCurrency(value) {
  if (value === null || value === undefined) return '—';
  if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatPercent(value) {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(1)}%`;
}

function formatVariantName(variant) {
  if (!variant) return '—';
  // Convert snake_case to Title Case for display
  return variant
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// =============================================================================
// SUMMARY CARD
// =============================================================================

function SummaryCard({ title, value, icon: Icon, color = 'indigo' }) {
  const colorClasses = {
    indigo: 'text-indigo-400 bg-indigo-500/10',
    green: 'text-emerald-400 bg-emerald-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
    pink: 'text-pink-400 bg-pink-500/10',
  };

  return (
    <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-800/50">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">{title}</p>
          <p className="text-xl font-bold text-white mt-0.5">{value}</p>
        </div>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MESSAGE VARIANT PERFORMANCE TABLE
// =============================================================================

function MessageVariantTable({ variants, sortBy, onSort }) {
  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return <ChevronDown className="w-3.5 h-3.5 inline ml-1" />;
  };

  const SortableHeader = ({ field, children, className = "" }) => (
    <TableHead 
      className={`text-slate-300 cursor-pointer hover:text-white transition-colors ${className}`}
      onClick={() => onSort(field)}
    >
      {children}
      <SortIcon field={field} />
    </TableHead>
  );

  if (variants.length === 0) {
    return (
      <div className="py-10 text-center" data-testid="message-variant-empty">
        <MessageSquare className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <h4 className="text-base font-medium text-slate-300 mb-1">No Message Variant Data</h4>
        <p className="text-slate-500 text-sm max-w-sm mx-auto">
          Message variant analytics appear when leads have message_variant set from campaigns/funnels.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800 hover:bg-transparent">
            <TableHead className="text-slate-300 w-[180px]">Message Variant</TableHead>
            <SortableHeader field="leads" className="text-right">Leads</SortableHeader>
            <SortableHeader field="appointments" className="text-right">Appts</SortableHeader>
            <SortableHeader field="shows" className="text-right">Shows</SortableHeader>
            <SortableHeader field="clients" className="text-right">Clients</SortableHeader>
            <SortableHeader field="revenue" className="text-right">Revenue</SortableHeader>
            <TableHead className="text-right text-slate-300">L→A</TableHead>
            <TableHead className="text-right text-slate-300">A→S</TableHead>
            <SortableHeader field="close_rate" className="text-right">S→C</SortableHeader>
            <SortableHeader field="revenue_per_lead" className="text-right">Rev/Lead</SortableHeader>
          </TableRow>
        </TableHeader>
        <TableBody>
          {variants.map((variant) => (
            <TableRow 
              key={variant.message_variant} 
              className={`border-slate-800/50 hover:bg-slate-800/30 ${
                variant.is_strongest ? 'bg-emerald-500/5' : 
                variant.is_weakest ? 'bg-amber-500/5' : ''
              }`}
              data-testid={`variant-row-${variant.message_variant}`}
            >
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <span className="text-slate-100 truncate max-w-[140px]" title={variant.message_variant}>
                    {formatVariantName(variant.message_variant)}
                  </span>
                  {variant.is_strongest && (
                    <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs py-0">
                      <Trophy className="w-3 h-3 mr-1" />Top
                    </Badge>
                  )}
                  {variant.is_weakest && (
                    <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs py-0">
                      <AlertTriangle className="w-3 h-3 mr-1" />Low
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">{variant.leads}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">{variant.appointments}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">{variant.shows}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">{variant.clients}</TableCell>
              <TableCell className="text-right font-mono text-emerald-400">{formatCurrency(variant.revenue)}</TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatPercent(variant.lead_to_appointment_rate)}
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatPercent(variant.appointment_to_show_rate)}
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatPercent(variant.show_to_close_rate)}
              </TableCell>
              <TableCell className="text-right font-mono text-slate-100">
                {formatCurrency(variant.revenue_per_lead)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function MessageVariantAnalytics({ 
  period = 'mtd', 
  workspaceId, 
  campaignId,
  vertical,
  channel,
  tenantId 
}) {
  const [sortBy, setSortBy] = useState('revenue');
  
  const query = useMessageVariantPerformance({
    period,
    sortBy,
    workspaceId,
    campaignId,
    vertical,
    channel,
    tenantId,
  });

  const variants = query.data?.variants || [];

  return (
    <div className="space-y-4" data-testid="message-variant-analytics">
      {/* Header - MA-ARCH-136A: Enhanced with helper text */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-pink-400" />
            Message Variant Analytics
          </h3>
          {/* MA-ARCH-136A: Helper text */}
          <p className="text-xs text-slate-500 mt-1">
            {HELPER_DEFINITIONS.message_variant}
          </p>
          <p className="text-sm text-slate-400 mt-1">
            Performance by messaging hypothesis across the full conversion chain
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard
          title="Variants"
          value={query.isLoading ? '...' : query.data?.total_variants || 0}
          icon={MessageSquare}
          color="pink"
        />
        <SummaryCard
          title="Total Leads"
          value={query.isLoading ? '...' : query.data?.total_leads || 0}
          icon={Users}
          color="blue"
        />
        <SummaryCard
          title="Total Revenue"
          value={query.isLoading ? '...' : formatCurrency(query.data?.total_revenue || 0)}
          icon={DollarSign}
          color="green"
        />
        <SummaryCard
          title="Avg/Variant"
          value={query.isLoading ? '...' : formatCurrency(query.data?.avg_revenue_per_variant || 0)}
          icon={Target}
          color="amber"
        />
      </div>

      {/* Sort Control */}
      <div className="flex justify-end">
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-44 bg-slate-800 border-slate-700 h-8 text-sm">
            <Filter className="w-3.5 h-3.5 mr-2 text-slate-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="revenue">Revenue</SelectItem>
            <SelectItem value="clients">Clients</SelectItem>
            <SelectItem value="leads">Leads</SelectItem>
            <SelectItem value="close_rate">Close Rate</SelectItem>
            <SelectItem value="revenue_per_lead">Rev/Lead</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Performance Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="py-10 text-center text-slate-400">
              <div className="animate-pulse">Loading message variants...</div>
            </div>
          ) : query.error ? (
            <div className="py-10 text-center">
              <MessageSquare className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-300 font-medium text-sm">Message Variant Analytics Unavailable</p>
              <p className="text-slate-500 text-xs mt-1">Unable to load message variant data at this time.</p>
            </div>
          ) : (
            <MessageVariantTable 
              variants={variants} 
              sortBy={sortBy}
              onSort={setSortBy}
            />
          )}
        </CardContent>
      </Card>

      {/* Data Note */}
      <div className="text-xs text-slate-500 text-center">
        * Uses snapshotted message_variant values. Records without message_variant are excluded.
      </div>
    </div>
  );
}
